/**
 * WanderAction - behavior tree action for random wandering behavior.
 */

import * as THREE from 'three';

import { NpcMovementComponent } from '../../../../../components/movement/NpcMovementComponent.js';
import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Blackboard } from '../../../blackboard/Blackboard.js';

export interface WanderActionOptions extends BehaviorActionOptions {
  wanderRadius?: number;
  minWaitTime?: number;
  maxWaitTime?: number;
  spawnPositionKey?: string;
}

@EngineClass('WanderAction')
export class WanderAction extends BehaviorAction {
  private wanderRadius: number;
  private minWaitTime: number;
  private maxWaitTime: number;
  private spawnPositionKey?: string;
  private spawnPosition?: THREE.Vector3;
  private currentTarget?: THREE.Vector3;
  private movementComponent: NpcMovementComponent | null = null;
  private isWaiting: boolean = false;
  private waitStartTime: number = 0;
  private waitDuration: number = 0;

  constructor(options: WanderActionOptions = {}) {
    super({
      name: 'Wander',
      ...options
    });

    this.wanderRadius = options.wanderRadius ?? 10.0;
    this.minWaitTime = options.minWaitTime ?? 2.0;
    this.maxWaitTime = options.maxWaitTime ?? 5.0;
    this.spawnPositionKey = options.spawnPositionKey ?? 'SpawnPosition';
  }

  protected override onInitialize(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return;
    }

    this.movementComponent = owner.getComponent(NpcMovementComponent);
    if (!this.movementComponent) {
      console.warn('WanderAction: No NpcMovementComponent found on actor');
      return;
    }

    // Get spawn position from blackboard or use current position
    if (this.spawnPositionKey) {
      this.spawnPosition = blackboard.getValue<THREE.Vector3>(this.spawnPositionKey);
    }

    if (!this.spawnPosition) {
      // Use current position as spawn position
      this.spawnPosition = new THREE.Vector3();
      owner.rootComponent.getWorldPosition(this.spawnPosition);

      // Store it in blackboard for future reference
      if (this.spawnPositionKey) {
        blackboard.setValue(this.spawnPositionKey, this.spawnPosition.clone());
      }
    }
  }

  protected override onEnter(blackboard: Blackboard): void {
    // Generate first wander target
    this.generateNewWanderTarget();
    this.isWaiting = false;
  }

  protected override async onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    const owner = this.getOwner(blackboard);
    if (!owner || !this.movementComponent || !this.spawnPosition) {
      return BehaviorStatus.Failure;
    }

    const currentTime = Date.now() / 1000;

    if (this.isWaiting) {
      // Currently waiting at a position
      const waitElapsed = currentTime - this.waitStartTime;
      if (waitElapsed >= this.waitDuration) {
        // Done waiting, generate new target
        this.generateNewWanderTarget();
        this.isWaiting = false;
      }
      return BehaviorStatus.Running;
    }

    // Check if we've reached our current target
    if (this.movementComponent.hasReachedCurrentTarget()) {
      // Start waiting
      this.isWaiting = true;
      this.waitStartTime = currentTime;
      this.waitDuration = this.minWaitTime + Math.random() * (this.maxWaitTime - this.minWaitTime);
      this.movementComponent.stop();
    }

    return BehaviorStatus.Running;
  }

  protected override onExit(blackboard: Blackboard, status: BehaviorStatus): void {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return;
    }

    // Stop movement
    this.movementComponent?.stop();
    this.isWaiting = false;
    this.currentTarget = undefined;
  }

  private generateNewWanderTarget(): void {
    if (!this.spawnPosition || !this.movementComponent) {
      return;
    }

    // Generate random point within wander radius
    const angle = Math.random() * Math.PI * 2;
    const distance = Math.random() * this.wanderRadius;

    this.currentTarget = new THREE.Vector3(
      this.spawnPosition.x + Math.cos(angle) * distance,
      this.spawnPosition.y,
      this.spawnPosition.z + Math.sin(angle) * distance
    );

    // Set the movement target
    this.movementComponent.setPath([this.currentTarget], false);
  }
}
